import React from "react";

interface PrivacyModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const PrivacyModal: React.FC<PrivacyModalProps> = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm">
      <div className="bg-white rounded-mediumRound shadow-lg max-w-4xl w-full mx-4 p-8 relative max-h-[90vh] overflow-hidden">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-500 hover:text-gray-800 z-10"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-6 w-6"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>

        {/* Content with scroll */}
        <div className="max-h-[80vh] overflow-y-auto pr-4">
          <div className="mb-6">
            <h1 className="text-2xl font-bold text-primary mb-3">Privacy Policy</h1>
            <p className="text-base font-semibold text-gray-700 mb-2">Last updated: 2 July 2025</p>
            <p className="text-sm italic text-gray-600 mb-6">
              Compliant with Regulation (EU) 2016/679 ("GDPR") and Romanian Law 190/2018
            </p>
          </div>

          <div className="space-y-6 text-base text-gray-700 leading-relaxed">
            <section>
              <h2 className="text-lg font-semibold text-primary mb-3">1. Who We Are</h2>
              <p className="mb-3">
                This website — <strong>www.droneden.ro</strong> and its subdomains — is operated by{" "}
                <strong>ACEVISION CONSULTING S.R.L.</strong> ("DroneDen", "we", "us").
              </p>
              <ul className="text-sm space-y-1 ml-6 mb-4">
                <li>• <strong>Registered office:</strong> Str. Mihail Kogălniceanu nr. 4E, Sat Florești, Jud. Cluj, Romania</li>
                <li>• <strong>Company ID:</strong> CUI 46912927, J12/6029/2022</li>
                <li>• <strong>Privacy contact:</strong> <a href="mailto:contact@droneden.ro" className="text-primary underline">contact@droneden.ro</a> | Tel: +40 749 148 608</li>
              </ul>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-primary mb-3">2. What Data We Collect and Why</h2>
              
              <div className="space-y-4">
                <div>
                  <h3 className="font-semibold text-gray-800 mb-2">Identification Data</h3>
                  <p className="text-sm mb-1"><strong>Includes:</strong> name, email, phone number, postal address</p>
                  <p className="text-sm mb-1"><strong>Purpose:</strong> Account creation, communication</p>
                  <p className="text-sm mb-1"><strong>Legal basis:</strong> Contract performance (GDPR Art. 6(1)(b))</p>
                  <p className="text-sm"><strong>Retention:</strong> 10 years after your last transaction</p>
                </div>

                <div>
                  <h3 className="font-semibold text-gray-800 mb-2">Professional Data</h3>
                  <p className="text-sm mb-1"><strong>Includes:</strong> PFA/SRL registration, drone licence, insurance details</p>
                  <p className="text-sm mb-1"><strong>Purpose:</strong> Verifying drone pilot eligibility, invoicing</p>
                  <p className="text-sm mb-1"><strong>Legal basis:</strong> Legal obligation (GDPR Art. 6(1)(c))</p>
                  <p className="text-sm"><strong>Retention:</strong> 10 years</p>
                </div>

                <div>
                  <h3 className="font-semibold text-gray-800 mb-2">Financial Data</h3>
                  <p className="text-sm mb-1"><strong>Includes:</strong> Tokenised payment data (via Stripe) — full card details not visible to us</p>
                  <p className="text-sm mb-1"><strong>Purpose:</strong> Bookings and service fee processing</p>
                  <p className="text-sm mb-1"><strong>Legal basis:</strong> Contract performance</p>
                  <p className="text-sm"><strong>Retention:</strong> As per Stripe's terms</p>
                </div>

                <div>
                  <h3 className="font-semibold text-gray-800 mb-2">Usage Data</h3>
                  <p className="text-sm mb-1"><strong>Includes:</strong> IP address, device info, logs, cookies, chat history</p>
                  <p className="text-sm mb-1"><strong>Purpose:</strong> Security, fraud prevention, analytics</p>
                  <p className="text-sm mb-1"><strong>Legal basis:</strong> Legitimate interests (GDPR Art. 6(1)(f))</p>
                  <p className="text-sm"><strong>Retention:</strong> Up to 3 years</p>
                </div>

                <div>
                  <h3 className="font-semibold text-gray-800 mb-2">Marketing Preferences</h3>
                  <p className="text-sm mb-1"><strong>Includes:</strong> Newsletter opt-in, social media handles</p>
                  <p className="text-sm mb-1"><strong>Purpose:</strong> Sending promotional messages</p>
                  <p className="text-sm mb-1"><strong>Legal basis:</strong> Consent (GDPR Art. 6(1)(a))</p>
                  <p className="text-sm"><strong>Retention:</strong> Until consent is withdrawn</p>
                </div>
              </div>

              <p className="text-sm mt-4 font-medium">
                <strong>Note:</strong> Invoices and accounting records are kept for 10 years under the Romanian Fiscal Code (Art. 319(20)).
              </p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-primary mb-3">3. How We Use Your Data</h2>
              <ul className="text-sm space-y-1 ml-6">
                <li>• Creating and managing your user account</li>
                <li>• Enabling bookings, messaging, and payments via Stripe Connect</li>
                <li>• Complying with KYC and anti-money laundering (AML) rules</li>
                <li>• Resolving disputes and enforcing our Terms of Service</li>
                <li>• Sending marketing emails only if you have opted in</li>
                <li>• Improving platform performance through aggregated analytics</li>
              </ul>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-primary mb-3">4. Who We Share Data With</h2>
              <p className="text-sm mb-3">We use trusted third-party processors who comply with GDPR standards:</p>
              <ul className="text-sm space-y-1 ml-6 mb-4">
                <li>• <strong>Stripe Payments Europe, Ltd.</strong> — Payment processing and KYC (servers in Ireland)</li>
                <li>• <strong>Amazon Web Services EMEA SARL</strong> — Primary hosting (servers in Frankfurt, DE)</li>
                <li>• <strong>Microsoft Azure</strong> — Encrypted backups and compute (servers in the Netherlands)</li>
                <li>• <strong>Meta Platforms Ireland Ltd.</strong> — Social media integrations (Facebook, Instagram)</li>
                <li>• <strong>Google Ireland Ltd.</strong> — Aggregated analytics</li>
              </ul>
              <blockquote className="border-l-4 border-primary pl-4 italic text-sm text-gray-600">
                <strong>Note:</strong> All data stays within the European Economic Area (EEA). If transfers outside the EEA are required in the future, we will use Standard Contractual Clauses or equivalent safeguards.
              </blockquote>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-primary mb-3">5. Cookies</h2>
              <p className="text-sm mb-2">DroneDen uses:</p>
              <ul className="text-sm space-y-1 ml-6 mb-3">
                <li>• <strong>Essential cookies</strong> – for website functionality</li>
                <li>• <strong>Analytics cookies</strong> – to analyze usage patterns</li>
                <li>• <strong>Marketing cookies</strong> – for personalized ads</li>
              </ul>
              <p className="text-sm">The cookie banner allows you to accept, refuse, or customize non-essential cookies anytime.</p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-primary mb-3">6. Data Security</h2>
              <ul className="text-sm space-y-1 ml-6">
                <li>• TLS 1.3 encryption secures all traffic</li>
                <li>• Data stored in ISO 27001-certified data centers</li>
                <li>• Role-based access control restricts employee access</li>
                <li>• Independent security testing is conducted regularly</li>
              </ul>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-primary mb-3">7. Your Rights</h2>
              <p className="text-sm mb-2">Under GDPR, you may:</p>
              <ul className="text-sm space-y-1 ml-6 mb-3">
                <li>• Access your personal data</li>
                <li>• Correct inaccuracies</li>
                <li>• Request deletion ("right to be forgotten")</li>
                <li>• Restrict processing</li>
                <li>• Request a portable copy</li>
                <li>• Object to processing based on legitimate interest</li>
                <li>• Withdraw consent (for marketing)</li>
                <li>• File a complaint with <a href="https://www.dataprotection.ro" className="text-primary underline" target="_blank" rel="noopener noreferrer">ANSPDCP</a></li>
              </ul>
              <blockquote className="border-l-4 border-primary pl-4 italic text-sm text-gray-600">
                To exercise any of these rights, contact us at <a href="mailto:contact@droneden.ro" className="text-primary underline">contact@droneden.ro</a>. We respond within one month.
              </blockquote>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-primary mb-3">8. Automated Decision-Making</h2>
              <p className="text-sm">Stripe may conduct automated fraud checks. You may request human review if you disagree with the outcome.</p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-primary mb-3">9. Children</h2>
              <p className="text-sm">DroneDen is intended for users aged 18 and above. We do not knowingly collect data from minors.</p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-primary mb-3">10. Updates to This Policy</h2>
              <p className="text-sm">We may update this policy periodically. Material changes will be communicated via email or your user Dashboard. The effective date at the top reflects the latest version.</p>
            </section>

            <div className="mt-8 p-4 bg-gray-100 rounded-lg">
              <p className="text-sm text-center font-semibold">
                <strong>Questions?</strong><br />
                Contact us at{" "}
                <a href="mailto:contact@droneden.ro" className="text-primary underline">
                  contact@droneden.ro
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyModal; 